// The app (docs/ARCHITECTURE.md §8): one AppShell per page and the app flow, started by a page entry (main.ts for
// players, tune/main.ts for the tuner) with the presentation tuning its wars run (tuning/link.ts). The flow is entered
// once the coastline/border assets are loaded: setup → war (one disposable Match per war) → a short hold on the decided war →
// results. A war against the computer runs in the local room (rematch with a new seed, or a new war back at setup); an
// online war goes through the matchmaking screen to an online war room (docs/ARCHITECTURE.md §15; another rival, or
// the menu). A page opened from a friend's challenge link offers that challenge once. A lost room connection or a war
// the room refuses ends in the explicit aborted state. Every uncaught Error on the page — a client fault, or a room
// fault the connection raises as a RoomFaultError — ends the flow in the crash report (§13); the browser still logs it.

import '../styles/app.css'
import { RpcError } from '@esengine/rpc'
import { InvariantViolation, type MatchSeed, type SkirmishSetup } from '@taxwar/net'
import type { MatchAssignment } from '@taxwar/online'
import { mountHud } from '../hud'
import { startMatch, startOnlineMatch, type Match, type MatchEnd } from '../match'
import type { InviteChallenge, ResultAction } from '../match/contracts'
import { TransportError } from '../net'
import { RoomConnectError, challengeCodeIn, edgeOriginOf, type OnlineWarRequest } from '../online'
import { loadGlobeGeo, type GlobeGeo } from '../render/geo/polylines'
import { initialVisualSettings, type VisualSettings } from '../render/visualSettings'
import { matchmakingScreen } from '../screens/online'
import { resultsScreen } from '../screens/results'
import { randomSeed, setupScreen } from '../screens/setup'
import type { TuningLink } from '../tuning/link'
import { linkArcade, type AppPhase, type ArcadeLink } from '../arcade/link'
import { createVfxLayer } from '../vfx'
import { AppShell } from './AppShell'
import type { Screen, ScreenFactory } from './screen'
import { warAbortedScreen } from './screens/WarAbortedScreen'
import { warScreen } from './screens/WarScreen'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const START_FAILED_TITLE = 'WAR COULD NOT START'
const CONNECTION_LOST_TITLE = 'CONNECTION LOST'
const CRASH_TITLE = 'THE WAR CRASHED'
const CRASH_DETAIL = 'Something broke that should not have. Include this report and the seed when you tell us about it.'
/** How long the decided war stays on screen so its final impacts play out before the results (GDD §10). */
const RESULTS_HOLD_MS = 2500
const EDGE = edgeOriginOf(location)

/** Where a war came from: the local room (a setup to replay) or an online search (a request to search again). */
type WarOrigin = { readonly kind: 'local'; readonly setup: SkirmishSetup } | { readonly kind: 'online'; readonly request: OnlineWarRequest }

/** One war start in flight; its identity tells a live start from one a crash abandoned. */
interface Starting {
  readonly kind: 'starting'
  readonly origin: WarOrigin
}

type FlowState =
  | { readonly kind: 'setup' }
  | { readonly kind: 'matchmaking'; readonly request: OnlineWarRequest }
  | Starting
  | { readonly kind: 'war'; readonly match: Match; readonly origin: WarOrigin }
  | { readonly kind: 'concluding'; readonly match: Match; readonly origin: WarOrigin; readonly timer: ReturnType<typeof setTimeout> }
  | { readonly kind: 'ended' }
  | { readonly kind: 'crashed' }

/** The phase the embedding Arcade sees in each flow state (arcade/link.ts): a war on screen, or the menus around it. */
const PHASE_OF: Readonly<Record<FlowState['kind'], AppPhase>> = {
  setup: 'menu',
  matchmaking: 'menu',
  starting: 'menu',
  war: 'war',
  concluding: 'war',
  ended: 'menu',
  crashed: 'menu',
}

class WarFlow {
  private state: FlowState = { kind: 'setup' }

  constructor(
    private readonly shell: AppShell,
    private readonly geo: GlobeGeo,
    /** A friend's challenge the page was opened with: offered on the first setup screen only. */
    private challenge: InviteChallenge | null,
    private readonly arcade: ArcadeLink,
  ) {}

  /** Every state change: the embedding Arcade follows the phase from here, and only from here. */
  private moveTo(state: FlowState): void {
    this.state = state
    this.arcade.enter(PHASE_OF[state.kind])
  }

  readonly showSetup = (): void => {
    // A war being created replaces the current screen once it is up; the start in flight wins over "new war".
    if (this.state.kind === 'starting') return
    if (this.state.kind === 'war' || this.state.kind === 'concluding') throw new InvariantViolation(`setup shown in the ${this.state.kind} state`)
    this.moveTo({ kind: 'setup' })
    const challenge = this.challenge
    this.challenge = null
    this.shell.show(setupScreen({ startLocal: this.startWar, startOnline: this.findRival }, challenge))
  }

  private readonly startWar = (setup: SkirmishSetup): void => {
    // A second Start while the first war is still being created is the same request.
    if (this.state.kind === 'starting') return
    if (this.state.kind === 'war' || this.state.kind === 'concluding') throw new InvariantViolation(`war started in the ${this.state.kind} state`)
    const starting: Starting = { kind: 'starting', origin: { kind: 'local', setup } }
    this.moveTo(starting)
    void startMatch(setup).then(
      (match) => this.enterWar(starting, match),
      (error: unknown) => this.startFailed(starting, error),
    )
  }

  /** Online: the matchmaking screen searches; a found match opens its war room. */
  private readonly findRival = (request: OnlineWarRequest): void => {
    if (this.state.kind !== 'setup' && this.state.kind !== 'ended') throw new InvariantViolation(`online search started in the ${this.state.kind} state`)
    this.moveTo({ kind: 'matchmaking', request })
    this.shell.show(matchmakingScreen(request, EDGE, (assignment) => this.rivalFound(request, assignment), this.showSetup))
  }

  private rivalFound(request: OnlineWarRequest, assignment: MatchAssignment): void {
    // The player may have left the matchmaking screen as the match was made.
    if (this.state.kind !== 'matchmaking' || this.state.request !== request) return
    const starting: Starting = { kind: 'starting', origin: { kind: 'online', request } }
    this.moveTo(starting)
    void startOnlineMatch(assignment.roomUrl).then(
      (match) => this.enterWar(starting, match),
      (error: unknown) => this.startFailed(starting, error),
    )
  }

  private enterWar(starting: Starting, match: Match): void {
    // Only a crash leaves the starting state before the start settles (setup and start wait for it): release the war.
    if (this.state !== starting) {
      match.dispose()
      return
    }
    const origin = starting.origin
    this.moveTo({ kind: 'war', match, origin })
    this.shell.show(warScreen(this.geo, match, mountHud, createVfxLayer))
    // After the war screen is up: an end that already happened is delivered at once.
    match.onEnd((end) => this.endWar(match, origin, end))
  }

  /** A decided war holds on the war screen before the results; a lost one goes straight to the aborted state. */
  private endWar(match: Match, origin: WarOrigin, end: MatchEnd): void {
    const state = this.state
    if (state.kind !== 'war' || state.match !== match) throw new InvariantViolation('a war ended that is not the running one')
    if (end.kind === 'decided') {
      this.moveTo({ kind: 'concluding', match, origin, timer: setTimeout(() => this.showResults(match, origin), RESULTS_HOLD_MS) })
      return
    }
    this.moveTo({ kind: 'ended' })
    this.leaveWar(match, warAbortedScreen({ title: CONNECTION_LOST_TITLE, detail: end.error.message, report: null }, this.showSetup))
  }

  private showResults(match: Match, origin: WarOrigin): void {
    const state = this.state
    if (state.kind !== 'concluding' || state.match !== match) throw new InvariantViolation(`results shown in the ${state.kind} state`)
    this.moveTo({ kind: 'ended' })
    const [primary, secondary] = this.nextActions(origin)
    this.leaveWar(match, resultsScreen(match.report(), primary, secondary))
  }

  /**
   * After a war against the computer: a rematch replays the same setup (mode, nations, seats) on a new map, or a new
   * war. After an online war: another rival for the same nation (a quick match), or the menu.
   */
  private nextActions(origin: WarOrigin): readonly [ResultAction, ResultAction] {
    if (origin.kind === 'local') {
      return [
        { label: 'Rematch', run: () => this.startWar({ ...origin.setup, seed: randomSeed() }) },
        { label: 'New war', run: this.showSetup },
      ]
    }
    const again: OnlineWarRequest = { ...origin.request, entry: { kind: 'quick' } }
    return [
      { label: 'Find another rival', run: () => this.findRival(again) },
      { label: 'Main menu', run: this.showSetup },
    ]
  }

  /** Leaves the war screen (detaching its systems and listeners), then releases the Match and its room. */
  private leaveWar(match: Match, next: ScreenFactory<Screen>): void {
    this.shell.show(next)
    match.dispose()
  }

  /**
   * The room refused the setup (RpcError), the room failed while starting (TransportError), or an online room's socket
   * never opened (RoomConnectError); anything else is a bug. A start a crash abandoned has nothing left to show: its
   * failure is that crash's consequence.
   */
  private startFailed(starting: Starting, error: unknown): void {
    if (!(error instanceof RpcError) && !(error instanceof TransportError) && !(error instanceof RoomConnectError)) throw error
    if (this.state !== starting) return
    this.moveTo({ kind: 'ended' })
    this.shell.show(warAbortedScreen({ title: START_FAILED_TITLE, detail: error.message, report: null }, this.showSetup))
  }

  /**
   * An uncaught Error ends the flow in the crash report: the war (if any) is released and the player can go back to
   * setup. Only the first crash is reported on screen; later ones are still logged by the browser.
   */
  readonly crash = (error: Error): void => {
    const state = this.state
    if (state.kind === 'crashed') return
    this.moveTo({ kind: 'crashed' })
    this.shell.show(warAbortedScreen({ title: CRASH_TITLE, detail: CRASH_DETAIL, report: crashReport(error, seedOf(state)) }, this.showSetup))
    if (state.kind === 'concluding') clearTimeout(state.timer)
    if (state.kind === 'war' || state.kind === 'concluding') state.match.dispose()
  }
}

/** The seed that reproduces the war's map: the running war's, or the local setup being started. */
function seedOf(state: FlowState): MatchSeed | null {
  if (state.kind === 'war' || state.kind === 'concluding') return state.match.setup.seed
  if (state.kind === 'starting' && state.origin.kind === 'local') return state.origin.setup.seed
  return null
}

/** The copyable crash report: the error summary and, during a war, the seed that reproduces its map. */
function crashReport(error: Error, seed: MatchSeed | null): string {
  const summary = `${error.name}: ${error.message}`
  return seed === null ? summary : `${summary}\nseed ${seed}`
}

export interface AppOptions {
  /** The element the renderer canvas and every screen overlay go in. */
  readonly host: HTMLElement
  /** The presentation tuning every screen runs (the shell hands it to each screen, ScreenContext.tuning). */
  readonly tuning: TuningLink
}

/** The visual settings a page starts with (the player's reduced-motion preference sets "Reduce effects"). */
export function pageVisualSettings(): VisualSettings {
  return initialVisualSettings(window.matchMedia(REDUCED_MOTION_QUERY).matches)
}

/** Starts the app on a page: once per page (AppShell refuses a second one). */
export function startApp(options: AppOptions): void {
  const shell = new AppShell(options.host, pageVisualSettings(), options.tuning)
  // Loading until the flow shows its first screen; connecting never delays the game.
  const arcade = linkArcade(location.origin)

  // A friend's challenge link (?challenge=CODE) is offered once: the code leaves the address bar, so going back to the
  // menu (or reloading) does not offer a challenge that is already spent.
  const challengeCode = challengeCodeIn(location.search)
  const pageChallenge: InviteChallenge | null = challengeCode === null ? null : { code: challengeCode }
  if (challengeCode !== null) history.replaceState(null, '', location.pathname)

  // A load failure rejects unhandled on purpose: the globe cannot be drawn without its geo assets.
  void loadGlobeGeo().then((geo) => {
    const flow = new WarFlow(shell, geo, pageChallenge, arcade)
    // Only Error values are faults of ours; anything else (e.g. the browser's ResizeObserver notice) is left to the log.
    window.addEventListener('error', (event) => {
      if (event.error instanceof Error) flow.crash(event.error)
    })
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason instanceof Error) flow.crash(event.reason)
    })
    flow.showSetup()
  })
}
